/**
 * featureGates — configuración central del feature-gating por tier (NER-3161 / spec NER-3149).
 *
 * Fuente de verdad de gating en runtime = `subscription.limits.{xxxEnabled}` (+ grandfather).
 * Este archivo sólo cubre el PRE-CHECK del FE (useFeatureGate) y la resolución de copy/CTA
 * del FeatureGateModal. El 403 `FEATURE_GATE_REJECTED` del backend es la autoridad final:
 * el modal driven-by-interceptor usa el payload del 403 tal cual (requiredPlan/addonId/...).
 *
 * Mantener sync con:
 *  - services/subscriptions/gating-spec.md (secciones 2A, 4, 9)
 *  - services/.../middleware/checkSubscriptionLimit.js (checkFeatureEnabled)
 *  - NATIVE_PLAN_BY_FLAG / PLAN_AVAILABLE_ADDONS (abajo, usados por AddonsSection)
 */

// Evento global que dispara el interceptor de api.js cuando el backend rechaza con
// 403 FEATURE_GATE_REJECTED. FeatureGateProvider lo escucha y abre el modal.
export const FEATURE_GATE_EVENT = 'feature-gate-rejected';
export const FEATURE_GATE_ERROR_CODE = 'FEATURE_GATE_REJECTED';

// Jerarquía de planes. Los alias en castellano ('profesional') mapean al canónico FE
// ('professional') para que nunca rompa el display ni la comparación de niveles.
// `business` queda solo por clientes legacy: se retiró de la oferta y Profesional
// absorbió todas sus features (ningún gate requiere 'business').
export const PLAN_ORDER = { free: 1, starter: 2, professional: 3, business: 4, enterprise: 5 };
const PLAN_ALIASES = { profesional: 'professional' };

export const canonicalPlan = (planId) => {
  if (!planId) return planId;
  const p = String(planId).toLowerCase();
  return PLAN_ALIASES[p] || p;
};

export const planLevel = (planId) => PLAN_ORDER[canonicalPlan(planId)] || 0;

// Normaliza cualquier forma del nombre de feature al flag completo (`kdsEnabled`).
export const toFeatureFlag = (feature) => {
  if (!feature) return feature;
  return /Enabled$/.test(feature) ? feature : `${feature}Enabled`;
};

// Nombre base (sin sufijo Enabled), usado como clave de i18n (`featureGate.features.kds`).
export const toFeatureBase = (feature) => (feature ? feature.replace(/Enabled$/, '') : feature);

// Tier mínimo por feature (spec NER-3149 §2A). Alineado a los planId reales del seed.
// `paymentIntegrations` y `smartMenuPromos` NO se listan: son universal_free (sign-off Diego).
export const FEATURE_MIN_PLAN = {
  // Sprint 1 — Profesional+
  kdsEnabled: 'professional',
  staffIdentificationEnabled: 'professional',
  floorPlanEnabled: 'professional',
  cashRegisterEnabled: 'professional',
  afipInvoicingEnabled: 'professional',
  inventoryEnabled: 'professional',
  crmEnabled: 'professional',
  suppliersEnabled: 'professional',
  advancedReportsEnabled: 'professional',
  dataExportEnabled: 'professional',
  promotionsEnabled: 'professional',
  // Sprint 2 — ex Business, absorbidas por Profesional
  deliveryEnabled: 'professional',
  customerAppEnabled: 'professional',
  waitingQueueEnabled: 'professional',
  recipesEnabled: 'professional',
  privateAPIEnabled: 'professional',
  // Sprint 3 — Starter+
  takeawayEnabled: 'starter',
  // Sprint 4 — Enterprise
  multiRestaurantEnabled: 'enterprise',
  whitelabelEnabled: 'enterprise',
};

// Features comprables como add-on desde un plan inferior (spec §2A columna "Add-on").
// `id` es best-effort para el pre-check del hook; el 403 del backend manda el `addonId`
// autoritativo. `availableFrom` = plan mínimo desde el que se puede comprar el add-on.
// Delivery / Customer App / recetas ya no son add-on: vienen incluidas en Profesional.
// Starter solo puede sumar AFIP; Profesional solo "Restaurante adicional".
export const FEATURE_ADDON = {
  afipInvoicingEnabled: { id: 'afip-invoicing', availableFrom: 'starter' },
  multiRestaurantEnabled: { id: 'multi-restaurant', availableFrom: 'professional' },
};

/**
 * Evalúa el estado de gating de una feature contra una subscription (pre-check FE).
 * @param {string} feature - nombre lógico o flag (`kds` | `kdsEnabled`)
 * @param {object} subscription - record de SubscriptionContext
 * @returns {{enabled, requiresUpgrade, isGrandfathered, requiredPlan, addonAvailable, addonId, currentPlan, feature}}
 */
export const evaluateFeatureGate = (feature, subscription) => {
  const flag = toFeatureFlag(feature);
  const limits = subscription?.limits || {};
  const grandfathered = subscription?.grandfatheredFeatures || [];
  const currentPlan = canonicalPlan(subscription?.planId || 'free');

  const planEnabled = limits[flag] === true;
  const isGrandfathered = grandfathered.includes(flag);
  const enabled = planEnabled || isGrandfathered;

  const requiredPlan = FEATURE_MIN_PLAN[flag] || 'professional';
  const addon = FEATURE_ADDON[flag];
  const addonAvailable =
    !enabled &&
    !!addon &&
    planLevel(currentPlan) >= planLevel(addon.availableFrom) &&
    planLevel(currentPlan) < planLevel(requiredPlan);

  return {
    feature: flag,
    enabled,
    requiresUpgrade: !enabled,
    isGrandfathered,
    requiredPlan,
    addonAvailable,
    addonId: addonAvailable ? addon.id : null,
    currentPlan,
  };
};

/**
 * Features (flags) que un plan `targetPlan` desbloquea respecto de `currentPlan`.
 * Usado por la tabla comparativa del FeatureGateModal (variante upgrade_plan).
 * @returns {string[]} lista de flags ordenada por tier ascendente.
 */
export const featuresUnlockedBy = (targetPlan, currentPlan) => {
  const target = planLevel(targetPlan);
  const current = planLevel(currentPlan);
  return Object.keys(FEATURE_MIN_PLAN)
    .filter((flag) => {
      const lvl = planLevel(FEATURE_MIN_PLAN[flag]);
      return lvl > current && lvl <= target;
    })
    .sort((a, b) => planLevel(FEATURE_MIN_PLAN[a]) - planLevel(FEATURE_MIN_PLAN[b]));
};

/**
 * Dispara el modal de feature-gate de forma imperativa (pre-check del FE, sin pasar
 * por un 403). El FeatureGateHost escucha `FEATURE_GATE_EVENT` y abre el modal con
 * el mismo shape que el payload del 403 `FEATURE_GATE_REJECTED`.
 *
 * @param {object} payload - típicamente el resultado de `evaluateFeatureGate` o el
 *   body del 403. Debe incluir al menos `feature` (flag).
 */
export const openFeatureGate = (payload) => {
  if (typeof window === 'undefined' || !payload?.feature) return;
  window.dispatchEvent(new CustomEvent(FEATURE_GATE_EVENT, { detail: payload }));
};

// Add-ons vendibles por plan (fallback local de `plan.availableAddOns` del seed
// backend). Starter puede sumar AFIP; Profesional solo "Restaurante adicional".
export const PLAN_AVAILABLE_ADDONS = {
  free: [],
  starter: ['afip-invoicing'],
  professional: ['extra-restaurant'],
  business: ['extra-restaurant'],
  enterprise: [],
};

/**
 * Add-ons que se pueden comprar/activar desde un plan. Prefiere
 * `plan.availableAddOns` si el backend lo manda; si no, PLAN_AVAILABLE_ADDONS.
 * @param {string} planId
 * @param {object} [plan] plan del backend (opcional)
 * @returns {string[]} addOnIds
 */
export const getAvailableAddOnIds = (planId, plan) => {
  if (Array.isArray(plan?.availableAddOns)) return plan.availableAddOns;
  return PLAN_AVAILABLE_ADDONS[canonicalPlan(planId)] || [];
};

// Plan que incluye nativamente cada feature flag (mantener sync con backend
// FEATURE_REQUIRED_PLAN). Si el plan actual es >= a este nivel, la feature viene
// incluida y no es un add-on. Profesional absorbió Business.
export const NATIVE_PLAN_BY_FLAG = {
  deliveryEnabled: 'professional',
  customerAppEnabled: 'professional',
  recipesEnabled: 'professional',
  afipInvoicingEnabled: 'professional',
};

/**
 * Clasifica el catálogo de add-ons para el plan actual:
 *  - sellable: add-ons activables desde este plan (en availableAddOns y no
 *    incluidos nativamente) + los que no tienen flags gateadas por tier
 *    (ej. extra-restaurant). AFIP en Starter cae acá.
 *  - included: features de tier que el plan ya incluye (AFIP en Profesional).
 *  - upgrade: features de tier que requieren subir de plan.
 * @param {Array<object>} addons catálogo de GET /addons
 * @param {string} currentPlanId
 * @param {string[]} availableAddOnIds resultado de getAvailableAddOnIds
 */
// Features de tier que se muestran siempre (incluidas / a desbloquear), aunque
// el backend haya desactivado su add-on (delivery, customer-app y
// advanced-inventory quedaron active:false al absorberlas Profesional).
// Nombre/descripción salen de i18n `planSelection.addons.items.{addOnId}`.
export const TIER_FEATURE_CARDS = [
  { addOnId: 'afip-invoicing', featureFlags: { afipInvoicingEnabled: true } },
  { addOnId: 'delivery', featureFlags: { deliveryEnabled: true } },
  { addOnId: 'customer-app', featureFlags: { customerAppEnabled: true } },
  { addOnId: 'advanced-inventory', featureFlags: { recipesEnabled: true } },
];

/** Catálogo del backend + las TIER_FEATURE_CARDS que no vengan en él. */
export const withTierFeatureCards = (addons) => {
  const list = Array.isArray(addons) ? [...addons] : [];
  const ids = new Set(list.map((a) => a.addOnId));
  TIER_FEATURE_CARDS.forEach((card) => {
    if (!ids.has(card.addOnId)) list.push(card);
  });
  return list;
};

export const classifyAddons = (addons, currentPlanId, availableAddOnIds = []) => {
  const current = planLevel(currentPlanId);
  const nativePlanFor = (addon) =>
    Object.keys(addon.featureFlags || {}).reduce((best, flag) => {
      const native = NATIVE_PLAN_BY_FLAG[flag];
      return native && planLevel(native) > planLevel(best) ? native : best;
    }, null);

  const result = { sellable: [], included: [], upgrade: [] };
  (addons || []).forEach((addon) => {
    const native = nativePlanFor(addon);
    if (!native) {
      result.sellable.push(addon);
    } else if (current >= planLevel(native)) {
      result.included.push({ addon, requiredPlan: native });
    } else if (availableAddOnIds.includes(addon.addOnId)) {
      result.sellable.push(addon);
    } else {
      result.upgrade.push({ addon, requiredPlan: native });
    }
  });
  return result;
};
